import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

const ToastContext = createContext(null);

export const ToastProvider = ({
  children,
  defaultDuration = 3000,
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map()); // id -> timeout

  // --- helpers ---
  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    ({
      title,
      description,
      type = "default",
      duration = defaultDuration,
      dismissible = true,
    }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => {
        const next = [
          ...prev,
          { id, title, description, type, duration, dismissible },
        ];
        return next.slice(-maxToasts);
      });

      // auto-dismiss
      if (duration && duration > 0) {
        const timer = setTimeout(() => remove(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [defaultDuration, maxToasts, remove]
  );

  const api = useMemo(() => {
    const base = (opts) => push(opts);
    base.success = (msg, opts = {}) =>
      push({ title: "Success", description: msg, type: "success", ...opts });
    base.error = (msg, opts = {}) =>
      push({ title: "Error", description: msg, type: "error", ...opts });
    base.info = (msg, opts = {}) =>
      push({ title: "Info", description: msg, type: "info", ...opts });
    base.remove = remove;
    return base;
  }, [push, remove]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onClose={remove} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};

// --- UI ---

const ToastViewport = ({ toasts, onClose }) => {
  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(
    <div
      aria-live="polite"
      aria-atomic="true"
      className="toast-viewport"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        display: "grid",
        gap: 12,
        zIndex: 9999,
        maxWidth: 360,
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={() => onClose(t.id)} />
      ))}
    </div>,
    document.body
  );
};

const ToastItem = ({ toast, onClose }) => {
  const palette = {
    default: { border: "#e5e7eb", bg: "#ffffff", text: "#111827" },
    success: { border: "#10b981", bg: "#ecfdf5", text: "#065f46" },
    error: { border: "#ef4444", bg: "#fef2f2", text: "#7f1d1d" },
    info: { border: "#3b82f6", bg: "#eff6ff", text: "#1e3a8a" },
  }[toast.type || "default"];

  return (
    <div
      role="status"
      className="toast-item"
      style={{
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.text,
        borderRadius: 12,
        padding: "12px 14px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          {toast.title && (
            <div
              style={{
                fontWeight: 600,
                lineHeight: 1.2,
                marginBottom: toast.description ? 4 : 0,
              }}
            >
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div style={{ fontSize: 14, lineHeight: 1.35 }}>
              {toast.description}
            </div>
          )}
        </div>

        {toast.dismissible !== false && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: 4,
              color: palette.text,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default ToastProvider;
